import { Avatar, Button } from '@parsina/framework';
import { C } from '@parsina/framework/tokens';
import { LTR_TEXT } from '../../i18n';
import MobileCard, { MobileCardMeta } from '../MobileCard';

/**
 * One receipt, as a card.
 *
 * Render-only (docs/MOBILE-REDESIGN.md §4.7): every fact arrives already
 * decided — the amount pre-formatted in toman, the badges as `{label,bg,text}`,
 * the notes as finished sentences. The card never asks whether a payment may be
 * voided, never converts rials and never reaches for a hook. That is what stops
 * it drifting from the desktop table it is the phone's answer to.
 *
 * Three things it exists to make impossible.
 *
 * **A reversal that does not look like one.** A voided payment renders struck
 * through with its reason and its timestamp as VISIBLE TEXT. On the desktop
 * those two live in a `title`, which on a phone is nothing at all — and «this
 * money came back» is a money-correctness fact, which §2.4 rule 1 forbids
 * hiding.
 *
 * **A refund that reads as income.** A refund row carries a leading «−» and is
 * tinted `danger`, because a bare figure in the same green as everything above
 * it says the clinic took the money rather than gave it back.
 *
 * **A method with no name.** `badges` is rendered verbatim; the caller builds it
 * from the shared seven-method vocabulary, so no payment can arrive with a
 * blank where its method should be.
 *
 * On the anatomy: the card body is the tap target and the overflow menu is its
 * SIBLING, not its child. Making the whole card a button and hanging the menu
 * trigger inside it nests one control in another — the click bubbles, and a
 * screen reader announces a button whose accessible name contains a second
 * button's.
 */
export default function MobilePaymentCard({
  name,
  /** Sub-line parts — date, time, doctor, reference. Missing ones are omitted. */
  meta = [],
  /** Already formatted, already in toman. */
  amount,
  unit,
  /** Renders the leading «−» that says the money went the other way. */
  negative = false,
  struck = false,
  /** 'good' (money in) | 'danger' (money out) | 'muted' (void, or not yet real) */
  tone = 'good',
  /** `[{ key, label, bg, text }]` — method, invoice status, transaction status. */
  badges = [],
  /** Finished sentences shown under the badges: a void reason, a partial refund. */
  notes = [],
  onOpen,
  openLabel,
  /** A `MobileRowMenu` element, or null when the viewer may do nothing here. */
  menu,
  'data-testid': testId,
}) {
  const amountColor = tone === 'danger' ? C.red : tone === 'muted' ? C.textMuted : C.green;

  const body = (
    <>
      <Avatar name={name} size={36} />

      <span style={{ flex: 1, minWidth: 0, display: 'block' }}>
        <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.5rem' }}>
          <span
            dir="auto"
            style={{
              fontSize: '0.8125rem', fontWeight: 700,
              color: tone === 'muted' ? C.textMuted : C.textPrimary,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {name}
          </span>

          {/* Money is an LTR island: without the isolation the «−» and the unit
              are dragged to the wrong end of the figure inside Persian text. */}
          <span
            {...LTR_TEXT}
            style={{
              flexShrink: 0,
              fontSize: '0.875rem', fontWeight: 800,
              color: amountColor,
              fontVariantNumeric: 'tabular-nums',
              textDecoration: struck ? 'line-through' : undefined,
              whiteSpace: 'nowrap',
            }}
          >
            {negative ? '−' : ''}{amount}
            {unit ? (
              <span style={{ fontSize: '0.6875rem', fontWeight: 500, color: C.textMuted, marginInlineStart: '0.1875rem' }}>
                {unit}
              </span>
            ) : null}
          </span>
        </span>

        <MobileCardMeta parts={meta} />

        {badges.length > 0 && (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.375rem' }}>
            {badges.map((b) => (
              <span
                key={b.key}
                data-testid={testId ? `${testId}-badge-${b.key}` : undefined}
                style={{
                  padding: '0.125rem 0.5rem', borderRadius: '999px',
                  fontSize: '0.625rem', fontWeight: 700,
                  background: b.bg, color: b.text, whiteSpace: 'nowrap',
                }}
              >
                {b.label}
              </span>
            ))}
          </span>
        )}

        {/* The «why» a desktop hides in a tooltip. A phone has no hover, so a
            reason that is not text is a reason nobody will ever read. */}
        {notes.filter(Boolean).map((note, i) => (
          <span
            key={i}
            style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.6875rem', color: C.textMuted, lineHeight: 1.6 }}
          >
            {note}
          </span>
        ))}
      </span>
    </>
  );

  return (
    <MobileCard
      muted={tone === 'muted'}
      marker={tone === 'danger' ? C.red : undefined}
      data-testid={testId}
      data-state={tone}
      style={{ display: 'flex', alignItems: 'flex-start', gap: '0.375rem', padding: '0.5rem 0.5rem 0.5rem 0.375rem' }}
    >
      {onOpen ? (
        <Button
          variant="ghost"
          onClick={onOpen}
          aria-label={openLabel}
          data-testid={testId ? `${testId}-open` : undefined}
          className="flex-1 min-w-0 justify-start items-start gap-2.5 whitespace-normal"
          style={{ height: 'auto', minHeight: '2.75rem', padding: '0.375rem 0.5rem', textAlign: 'start' }}
        >
          {body}
        </Button>
      ) : (
        <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: '0.625rem', padding: '0.375rem 0.5rem' }}>
          {body}
        </span>
      )}

      {menu || null}
    </MobileCard>
  );
}
